import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TokenGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldAlt, faCheckCircle, faRedo } from "@fortawesome/free-solid-svg-icons"; // Added icons

const OtpVerification = () => {
  const navigate = useNavigate();
  const [otpValues, setOtpValues] = useState(["", "", "", "", ""]);
  const [formData, setFormData] = useState({
    code: "",
  });
  const refs = useRef([]);

  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (index, value) => {
    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, '');

    const newOtpValues = [...otpValues];

    // Handle backspace to navigate to the previous input if the current field is empty
    if (numericValue.length === 0 && newOtpValues[index] !== "") {
      newOtpValues[index] = "";
      setOtpValues(newOtpValues);
      if (index > 0) {
        refs.current[index - 1].focus();
      }
      return;
    }

    // Prevent more than one character in an input
    if (numericValue.length > 1) return;

    newOtpValues[index] = numericValue;
    setOtpValues(newOtpValues);

    const otpCode = newOtpValues.join("");
    setFormData({ ...formData, code: otpCode });

    // Move focus to the next input box if a value is entered and it's not the last input
    if (numericValue && index < 4) {
      refs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault(); // Prevent default paste behavior
    const pastedData = e.clipboardData.getData("text").trim().replace(/[^0-9]/g, ''); // Trim and keep only numbers
    
    if (pastedData.length === 5) {
      const newOtpValues = pastedData.split("");
      setOtpValues(newOtpValues);
      setFormData({ ...formData, code: pastedData });
      refs.current[4].focus(); // Focus the last input field
    } else if (pastedData.length < 5) {
        // If pasted data is less than 5, fill what's available and clear the rest
        const newOtpValues = Array(5).fill("");
        pastedData.split("").forEach((char, i) => {
            if (i < 5) newOtpValues[i] = char;
        });
        setOtpValues(newOtpValues);
        setFormData({ ...formData, code: newOtpValues.join("") });
        // Focus the last filled or the first empty field
        refs.current[Math.min(pastedData.length, 4)].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace when input is empty to move to previous field
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      e.preventDefault(); // Prevent default backspace behavior (e.g., browser navigation)
      refs.current[index - 1].focus();
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (formData.code.length !== 5) {
      toast.error("Please enter the complete 5-digit OTP.");
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please log in again.");
        setIsLoading(false);
        navigate("/");
        return;
      }

      const response = await axios.post(
        "https://backend.inyumba.co.ke/iNyumba/auth/otp",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.response && response.data.response.token) {
        localStorage.setItem("token", response.data.response.token);
        localStorage.setItem(
          "user",
          JSON.stringify(response.data.response.user)
        );
      }

      toast.success("OTP verification successful!");
      navigate("/user/landingpage");
    } catch (error) {
      console.error("OTP Verification error:", error);
      if (error.response) {
        if (error.response.status === 401) {
          toast.error("Invalid OTP or session expired. Please try again.");
        } else if (error.response.data && error.response.data.response) {
          toast.error(error.response.data.response);
        } else {
          toast.error("An error occurred during verification. Please try again.");
        }
      } else {
        toast.error("Network error or server unreachable. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please log in again.");
        setIsLoading(false);
        navigate("/");
        return;
      }

      await axios.post(
        "https://backend.inyumba.co.ke/iNyumba/auth/resend-otp",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("OTP resent successfully! Check your email.");
      setOtpValues(["", "", "", "", ""]); // Clear OTP fields after resend
      refs.current[0].focus(); // Focus the first input field
    } catch (error) {
      console.error("Resend OTP error:", error);
      if (error.response && error.response.data && error.response.data.response) {
        toast.error(error.response.data.response);
      } else {
        toast.error("An error occurred while resending OTP. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TokenGuard>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 p-8 text-center space-y-8 animate-fade-in-down">
          <div className="text-center">
            <FontAwesomeIcon icon={faShieldAlt} className="text-indigo-500 text-6xl mx-auto mb-4" />
            <h2 className="text-4xl font-extrabold text-gray-900 mb-2">
              Verify Your Account
            </h2>
            <p className="text-gray-600 text-lg">
              Please enter the 5-digit code sent to your email.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="flex justify-center space-x-4">
              {otpValues.map((value, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={value}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-14 h-14 text-center border-2 border-indigo-300 rounded-xl focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-3xl font-bold text-gray-800 transition-all duration-200"
                  ref={(input) => (refs.current[index] = input)}
                  aria-label={`OTP digit ${index + 1}`}
                />
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-center sm:space-x-4 space-y-4 sm:space-y-0">
              <button
                type="submit"
                disabled={isLoading || formData.code.length !== 5}
                className={`flex items-center justify-center space-x-3 w-full sm:w-auto py-3 px-6 rounded-lg text-lg font-semibold transition-all duration-300 ease-in-out shadow-md
                  ${isLoading || formData.code.length !== 5
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-indigo-600 to-purple-700 text-white hover:from-indigo-700 hover:to-purple-800 transform hover:scale-105"
                  }`}
              >
                {isLoading ? (
                  <>
                    <Oval
                      height={20}
                      width={20}
                      color="white"
                      ariaLabel="loading-spinner"
                      secondaryColor="rgba(255,255,255,0.4)"
                      strokeWidth={4}
                      strokeWidthSecondary={2}
                    />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Verify OTP</span>
                  </>
                )}
              </button>

              <button
                type="button" // Change to type="button" to prevent form submission
                onClick={handleResendOtp}
                disabled={isLoading}
                className={`flex items-center justify-center space-x-3 w-full sm:w-auto py-3 px-6 rounded-lg text-lg font-semibold transition-all duration-300 ease-in-out shadow-sm
                  ${isLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300 transform hover:scale-105"
                  }`}
              >
                {isLoading ? (
                  <>
                    <Oval
                      height={20}
                      width={20}
                      color="currentColor" // Use currentColor to inherit text color
                      ariaLabel="loading-spinner"
                      secondaryColor="rgba(0,0,0,0.2)"
                      strokeWidth={4}
                      strokeWidthSecondary={2}
                    />
                    <span>Resending...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faRedo} />
                    <span>Resend OTP</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </TokenGuard>
  );
};

export default OtpVerification;